import { filter, uniq } from "lodash-es";
import { useEffect } from "react";
import { action, computed, makeObservable, observable } from "mobx";

import { MultiSelectActions } from "@/components/multi-select-actions/multi-select-actions";
import { APPEARANCES_WITHOUT_SEPARATOR_SET } from "@/components/item-list/constants";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection/add-to-collection-modal-store";
import { AddToCollectionModal } from "@/components/modal/add-to-collection/add-to-collection-modal";
import { RemoveNotesFromSharedCollectionModal } from "@/components/modal/remove-notes-from-shared-collection/remove-notes-from-shared-collection-modal";
import { RemoveNotesFromSharedCollectionModalStore } from "@/components/modal/remove-notes-from-shared-collection/remove-notes-from-shared-collection-modal-store";
import { DeleteSharedNotesModalStore } from "@/components/modal/delete-shared-notes/delete-shared-notes-modal-store";
import { INoteObservable } from "@/store/note";
import { actions } from "@/actions";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { DeleteSharedNotesModal } from "@/components/modal/delete-shared-notes/delete-shared-notes-modal";
import { DeleteNotePermanentlyModalStore } from "@/components/modal/delete-notes-permanently/delete-notes-permanently-modal-store";
import { IItemListState } from "@/components/item-list/types";
import { EventContext } from "@/domains/metrics/context";
import { MDSRowItemAppearance } from "@/mds";
import { itemsModule } from "@/modules/items";
import { notesModule } from "@/modules/notes";
import { toastModule } from "@/modules/toast";
import { DeleteCollectionsModal } from "@/components/modal/delete-collections/delete-collections-modal";
import { DeleteCollectionsModalStore } from "@/components/modal/delete-collections/delete-collections-modal-store";

export interface ListStateProvider {
  orderedItemIds: string[];
  collectionMultiSelectActionsContext?: CollectionObservable;
  supportsMoveNoteToTrashListAction?: boolean;
  supportsDeleteCollectionsListAction?: boolean;
  supportsRestoreListAction?: boolean;
  supportsAddToCollectionListAction?: boolean;
  supportsRemoveFromCollectionListAction?: boolean;
  supportsChatListAction?: boolean;
  supportsAutoOrganizeListAction?: boolean;
  getChatNoteIdsFromSelectedItems?: (selectedItemIds: string[]) => string[];
  getChatCollectionIdsFromSelectedItems?: (selectedItemIds: string[]) => string[];
  eventContext: EventContext;
}

export class ListStateObservable extends AppSubStore implements IItemListState {
  anchorSelectedItemId?: string;
  selectedItemIds: string[] = [];
  highlightedItemId?: string;
  listStateProvider: ListStateProvider;
  deleteNotePermanentlyModal?: DeleteNotePermanentlyModalStore;
  isCopying = false;
  readonly canSelect: boolean;

  constructor({
    listStateProvider,
    deleteNotePermanentlyModal,
    canSelect = true,
    ...injectedDeps
  }: AppSubStoreArgs & {
    listStateProvider: ListStateProvider;
    deleteNotePermanentlyModal?: DeleteNotePermanentlyModalStore;
    canSelect?: boolean;
  }) {
    super(injectedDeps);

    this.deleteNotePermanentlyModal = deleteNotePermanentlyModal;
    this.listStateProvider = listStateProvider;
    this.canSelect = canSelect;

    makeObservable<
      this,
      | "addToCollectionModalStore"
      | "deleteSharedNotesModalStore"
      | "removeFromCollectionModalStore"
      | "deleteCollectionsModalStore"
    >(this, {
      getNextItemId: true,
      shouldHideSeparator: false,
      canSelect: false,
      handleDeleteCollections: true,
      anchorSelectedItemId: observable,
      selectedItemIds: observable,
      highlightedItemId: observable,
      listStateProvider: observable,
      isCopying: observable,
      deleteNotePermanentlyModal: false,
      isHoverable: computed,
      isSelectModeActive: computed,
      canAddToCollection: computed,
      canRemoveFromCollection: computed,
      canMoveNoteToTrash: computed,
      canMoveCollectionToTrash: computed,
      canRestoreFromTrash: computed,
      canChatWithSelection: computed,
      canAutoOrganize: computed,
      multiSelectUi: computed,
      isSelected: false,
      toggleItemSelection: action,
      selectItemsFromLastSelectionUntil: action,
      clearSelection: action,
      addToCollectionModalStore: false,
      deleteSharedNotesModalStore: false,
      removeFromCollectionModalStore: false,
      highlightItem: action,
      handleAddToCollection: action,
      handleRemoveFromCollection: action,
      handleRestore: action,
      handleDeleteNote: action,
      handleCopySelectedNotes: action,
      handleAutoOrganize: action,
      handleChatWithSelection: action,
      reset: action,
      useListStateEffects: false,
      deleteCollectionsModalStore: false,
    });
  }

  get isHoverable() {
    return this.store.publicAppStore.interface.isHoverable;
  }

  get isSelectModeActive() {
    return this.selectedItemIds.length > 0;
  }

  get canAddToCollection() {
    return this.listStateProvider.supportsAddToCollectionListAction;
  }

  get canRemoveFromCollection() {
    return (
      !!this.listStateProvider.supportsRemoveFromCollectionListAction &&
      !!this.listStateProvider.collectionMultiSelectActionsContext
    );
  }

  get canMoveNoteToTrash() {
    return this.listStateProvider.supportsMoveNoteToTrashListAction;
  }

  get canMoveCollectionToTrash() {
    return this.listStateProvider.supportsDeleteCollectionsListAction;
  }

  get canRestoreFromTrash() {
    return this.listStateProvider.supportsRestoreListAction;
  }

  get canChatWithSelection() {
    return !!this.listStateProvider.supportsChatListAction && this.selectedItemIds.length > 0;
  }

  get canAutoOrganize() {
    const supportsAutoOrganize = this.listStateProvider.supportsAutoOrganizeListAction;
    return supportsAutoOrganize ?? true;
  }

  get multiSelectUi() {
    return (
      <>
        <MultiSelectActions listState={this} />
        <AddToCollectionModal store={this.addToCollectionModalStore} />
        <DeleteSharedNotesModal store={this.deleteSharedNotesModalStore} />
        <RemoveNotesFromSharedCollectionModal store={this.removeFromCollectionModalStore} />
        <DeleteCollectionsModal state={this.deleteCollectionsModalStore} />
      </>
    );
  }

  isSelected = (itemId: string) => this.selectedItemIds.includes(itemId);

  getNextItemId = (itemId: string) => {
    const index = this.listStateProvider.orderedItemIds.indexOf(itemId);
    if (index === -1) return;

    return this.listStateProvider.orderedItemIds[index + 1];
  };

  shouldHideSeparator = ({
    itemId,
    appearance,
  }: {
    itemId: string;
    appearance: MDSRowItemAppearance;
  }): boolean => {
    if (itemId === this.highlightedItemId) {
      return true;
    }

    if (this.isSelected(itemId)) {
      return true;
    }

    if (APPEARANCES_WITHOUT_SEPARATOR_SET.has(appearance)) {
      return true;
    }

    const nextItemId = this.getNextItemId(itemId);
    if (!nextItemId) {
      return false;
    }

    if (nextItemId === this.highlightedItemId) {
      return true;
    }

    if (this.isSelected(nextItemId)) {
      return true;
    }

    const nextAppearance = itemsModule.resolveAppearance({
      itemId: nextItemId,
      appStore: this.store,
    });

    if (APPEARANCES_WITHOUT_SEPARATOR_SET.has(nextAppearance)) {
      return true;
    }

    const nextNote = this.store.notes.get(nextItemId);
    return Boolean(nextNote?.suggestionData);
  };

  toggleItemSelection = (itemId: string) => {
    if (this.selectedItemIds.includes(itemId)) {
      this.selectedItemIds = this.selectedItemIds.filter(e => e !== itemId);
      return;
    }
    this.anchorSelectedItemId = itemId;
    this.selectedItemIds.push(itemId);
  };

  selectItemsFromLastSelectionUntil = (itemId: string) => {
    const { orderedItemIds } = this.listStateProvider;
    const lastSelectedIndex = this.anchorSelectedItemId
      ? orderedItemIds.indexOf(this.anchorSelectedItemId)
      : 0;
    const nextSelectedIndex = orderedItemIds.indexOf(itemId);

    const [startIndex, endIndex] =
      lastSelectedIndex < nextSelectedIndex
        ? [lastSelectedIndex, nextSelectedIndex + 1]
        : [nextSelectedIndex, lastSelectedIndex + 1];

    const newSelectedItems = orderedItemIds.slice(startIndex, endIndex);
    this.selectedItemIds = uniq([...this.selectedItemIds, ...newSelectedItems]);
  };

  clearSelection = () => {
    this.anchorSelectedItemId = undefined;
    this.selectedItemIds = [];
  };

  private addToCollectionModalStore = new AddToCollectionModalStore(this, {
    handleConfirm: this.clearSelection,
  });
  private deleteSharedNotesModalStore = new DeleteSharedNotesModalStore(this, {
    handleConfirm: this.clearSelection,
  });
  private removeFromCollectionModalStore = new RemoveNotesFromSharedCollectionModalStore(this, {
    handleConfirm: this.clearSelection,
  });
  private deleteCollectionsModalStore = new DeleteCollectionsModalStore(this, {
    onClose: this.clearSelection,
  });

  highlightItem = (itemId?: string) => {
    this.highlightedItemId = itemId;
  };

  handleAddToCollection = () => {
    this.addToCollectionModalStore.open({
      noteIds: this.selectedItemIds,
      collection: this.listStateProvider.collectionMultiSelectActionsContext,
      eventContext: this.listStateProvider.eventContext,
    });
  };

  handleAutoOrganize = () => {
    const noteIds = [...this.selectedItemIds];
    this.clearSelection();
    actions.autoOrganizeNotes({ noteIds, store: this.store });
  };

  handleChatWithSelection = async () => {
    const noteIdsFromProvider = this.listStateProvider.getChatNoteIdsFromSelectedItems
      ? this.listStateProvider.getChatNoteIdsFromSelectedItems(this.selectedItemIds)
      : this.selectedItemIds;

    const collectionIdsFromProvider = this.listStateProvider.getChatCollectionIdsFromSelectedItems
      ? this.listStateProvider.getChatCollectionIdsFromSelectedItems(this.selectedItemIds)
      : this.listStateProvider.collectionMultiSelectActionsContext
        ? [this.listStateProvider.collectionMultiSelectActionsContext.id]
        : [];

    const noteIds = Array.from(new Set(noteIdsFromProvider));
    const collectionIds = Array.from(new Set(collectionIdsFromProvider));

    this.clearSelection();
    await actions.startChatWithEntities({
      noteIds,
      collectionIds,
      store: this.store,
      overrideContext: false,
    });
  };

  handleRemoveFromCollection = () => {
    const collection = this.listStateProvider.collectionMultiSelectActionsContext;
    if (!collection) return;

    if (!collection.isShared) {
      const noteIds = this.selectedItemIds;

      this.clearSelection();
      actions.removeNotesFromCollection({
        collection,
        noteIds,
        store: this.store,
      });
      return;
    }

    const notes = filter(
      this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
    ) as INoteObservable[];
    this.removeFromCollectionModalStore.open({ collection, notes });
  };

  handleRestore = () => {
    const notes = filter(
      this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
    ) as INoteObservable[];
    this.clearSelection();
    actions.restoreNotesFromTrash({ notes, store: this.store });
  };

  handleDeleteNote = () => {
    if (this.deleteNotePermanentlyModal) {
      const notes = filter(
        this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
      ) as INoteObservable[];
      this.deleteNotePermanentlyModal.open({ notes });
      return;
    }

    const notes = filter(
      this.selectedItemIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
    ) as INoteObservable[];
    const isShared = notes.some(note => note.isShared);
    if (isShared) {
      this.deleteSharedNotesModalStore.open(...notes);
      return;
    }

    this.clearSelection();
    actions.moveNotesToTrash({ notes, store: this.store });
  };

  handleDeleteCollections = () => {
    const collections = filter(
      this.selectedItemIds.map(collectionId =>
        this.store.collections.getCollectionObservableById({ collectionId })
      )
    ) as CollectionObservable[];

    this.deleteCollectionsModalStore.open({ collectionIds: collections.map(c => c.id) });
  };

  handleCopySelectedNotes = async () => {
    if (this.isCopying) return;

    try {
      this.isCopying = true;
      // Get notes in the same order as they appear in the list
      const notes = this.listStateProvider.orderedItemIds
        .filter(id => this.selectedItemIds.includes(id))
        .map(noteId => this.store.notes.getNoteObservableById({ noteId }))
        .filter(note => note) as INoteObservable[];

      // Build XML string for each note
      const xmlStrings = await Promise.all(
        notes.map(async (note, index) => {
          // Wait for note content to load if not already loaded
          if (!note.isNoteContentDocumentLoaded) {
            await note.fetchNoteContentDocument();
          }

          // Get author names, falling back to "Unknown User" if not available
          const authors = note.authors
            .map(author => author.profileDisplayName || "Unknown User")
            .join(", ");

          // Get primary owner name, falling back to "Unknown User" if not available
          const primaryOwnerName = note.primaryOwner?.profileDisplayName || "Unknown User";

          // Get collection names
          const collections = note.collectionList?.allCollections
            .map(collection => collection.label)
            .join(", ");

          const content = note.noteContentDocument?.data?.remoteContent || "";
          const { mdx } = notesModule.convertEncodedContentToNoteContent(content);

          return `<note id="${note.id}" index="${index}">
  <head>
    <title>${note.title}</title>
    <created_at>${note.createdAt}</created_at>
    <created_by>${primaryOwnerName}</created_by>
    <last_edited_at>${note.modifiedAt}</last_edited_at>
    <edited_by>${authors}</edited_by>
    <in_collections>${collections}</in_collections>
  </head>
  <body>
    ${mdx}
  </body>
</note>`;
        })
      );

      // Combine all notes into a single XML string
      const combinedXml = xmlStrings.join("\n");

      // Add a small delay to make loading state visible
      await new Promise(resolve => setTimeout(resolve, 500));

      // Copy to clipboard
      await navigator.clipboard.writeText(combinedXml);

      // Show success toast
      toastModule.triggerToast({
        content: `${notes.length} note${notes.length === 1 ? "" : "s"} copied to clipboard`,
      });
    } finally {
      this.isCopying = false;
    }
  };

  reset = () => {
    this.anchorSelectedItemId = undefined;
    this.selectedItemIds = [];
    this.highlightedItemId = undefined;
    this.addToCollectionModalStore.close();
  };

  useListStateEffects() {
    useEffect(() => () => this.reset(), []);
  }
}
